'use client';

// Pro Terminal right-rail scanner. Streams intraday opportunities (scanner
// hits) as a compact, scrollable list: ticker, last/%chg, RVOL, and a
// color-coded reason tag. Clicking a row drives the terminal's active symbol.
//
// Data source — getIntradayOpportunities({ scope, limit }). The endpoint
// exposes a r1000 / all universe preset, so we surface
// it as the panel's preset selector. We refresh on a ~45s interval and pause
// polling while the tab is hidden (document.hidden) to avoid background churn.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Radar, RefreshCw, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  getIntradayOpportunities,
  getWatchlists,
  type IntradayOpportunitiesResponse,
  type OpportunityCandidate,
} from '@/lib/api-client';
import { resolvePanelTickers } from '@/lib/watchlistPanel';

interface ScannerPanelProps {
  activeSymbol: string;
  onSelectSymbol: (s: string) => void;
}

const REFRESH_MS = 45_000;
const ROW_LIMIT = 25;

type Scope = 'r1000' | 'all' | 'watchlist';

const SCOPE_PRESETS: ReadonlyArray<{ value: Scope; label: string; title: string }> = [
  { value: 'r1000', label: 'R1000', title: 'Russell 1000 — excludes sub-$5 / penny names' },
  { value: 'all', label: 'All', title: 'All stocks — widens the net' },
  { value: 'watchlist', label: 'Watchlist', title: 'Only symbols in your watchlists' },
];

type WinMode = 'open' | 'last30';

const WINDOW_PRESETS: ReadonlyArray<{ value: WinMode; label: string; title: string }> = [
  { value: 'open', label: '9:15–10:15', title: 'The opening-window scored scanner (9:15–10:15 ET)' },
  { value: 'last30', label: 'Last 30m', title: 'Live: events detected in the last 30 minutes' },
];

// Short, human reason tags. Mirrors the dashboard tile's mapping so the same
// scanner reasons read consistently across the app; unknown reasons fall back
// to the raw key.
const REASON_LABELS: Record<string, string> = {
  projected_rvol: 'Proj RVOL',
  recent_rvol: 'Recent RVOL',
  recent_rvol_confirmed: 'Recent RVOL ✓',
  velocity: 'Velocity',
  pressure: 'Pressure',
  gap_and_go: 'Gap & Go',
  gap_and_go_confirmed: 'Gap & Go ✓',
  orb_breakout: 'ORB',
  trend_continuation: 'Trend Cont.',
  gonogo_trend: 'Go/No-Go',
  confluence_stack: 'Confluence',
  live_signal_buy: 'Live Buy',
  live_signal_sell: 'Live Sell',
  eixom_cross_up: 'Eixom ↑',
  opening_drive: 'Open Drive',
};

// Color-code the reason tag by severity so the eye triages the list fast.
const SEVERITY_TAG: Record<string, string> = {
  extreme: 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
  high: 'bg-amber-500/15 text-amber-300 border border-amber-500/30',
  elevated: 'bg-sky-500/15 text-sky-300 border border-sky-500/30',
};

function reasonLabel(reason: string): string {
  return REASON_LABELS[reason] ?? reason;
}

function fmtPrice(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1000) return n.toFixed(0);
  if (n >= 100) return n.toFixed(1);
  return n.toFixed(2);
}

function fmtPct(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function fmtRvol(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(1)}×`;
}

// Detection time in ET (events are intraday; 24h HH:MM keeps the row compact).
function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false });
}

export function ScannerPanel({ activeSymbol, onSelectSymbol }: ScannerPanelProps) {
  const [scope, setScope] = useState<Scope>('r1000');
  const [winMode, setWinMode] = useState<WinMode>('open');
  const [data, setData] = useState<IntradayOpportunitiesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(false);

  // Guards a stale response from clobbering fresher state when the scope
  // changes mid-flight, and lets the cleanup function ignore in-flight loads.
  const reqIdRef = useRef(0);
  // Cached watchlist tickers for the 'watchlist' scope. Re-resolved on a full
  // load (scope switch / mount); reused across the 45s silent polls.
  const watchlistRef = useRef<string[] | null>(null);
  const [noWatchlist, setNoWatchlist] = useState(false);

  const load = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      const reqId = ++reqIdRef.current;
      if (opts.silent) setRefreshing(true);
      else setLoading(true);
      try {
        let res: IntradayOpportunitiesResponse;
        if (scope === 'watchlist') {
          // Resolve the user's watchlist tickers and let the server filter to
          // them — it applies the ticker set BEFORE the top-N slice, so a
          // low-ranked name still surfaces (a client-side filter of a capped
          // list would drop it). Cache across silent polls; re-resolve on a
          // full load (scope switch / mount).
          let tickers = watchlistRef.current;
          if (tickers == null || !opts.silent) {
            const lists = await getWatchlists();
            if (reqId !== reqIdRef.current) return; // superseded
            tickers = resolvePanelTickers(lists.filter((l) => !l.muted), 'all').slice(0, 200);
            watchlistRef.current = tickers;
          }
          if (tickers.length === 0) {
            if (reqId !== reqIdRef.current) return;
            setNoWatchlist(true);
            setData({ window: 'recap', sessionDate: null, universe: 'watchlist', count: 0, candidates: [] });
            setError(false);
            return;
          }
          setNoWatchlist(false);
          res = await getIntradayOpportunities({ tickers, limit: ROW_LIMIT, window: winMode });
        } else {
          setNoWatchlist(false);
          res = await getIntradayOpportunities({ scope, limit: ROW_LIMIT, window: winMode });
        }
        if (reqId !== reqIdRef.current) return; // superseded
        setData(res);
        setError(false);
      } catch {
        if (reqId !== reqIdRef.current) return;
        setError(true);
      } finally {
        if (reqId === reqIdRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [scope, winMode],
  );

  // Fetch on mount + whenever the scope preset changes.
  useEffect(() => {
    void load();
    // load is stable per (scope, window); re-runs when either preset changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, winMode]);

  // ~45s refresh, paused while the tab is hidden. Catches up with one fetch
  // when the tab becomes visible again so a backgrounded panel isn't stale.
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer != null) return;
      timer = setInterval(() => {
        if (!document.hidden) void load({ silent: true });
      }, REFRESH_MS);
    };
    const stop = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        void load({ silent: true });
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load]);

  // Most-recently-detected first (the API ranks by score; the user wants a
  // chronological feed). detectedAt is ISO UTC, so a lexicographic compare is
  // chronological.
  const candidates = [...(data?.candidates ?? [])].sort((a, b) => b.detectedAt.localeCompare(a.detectedAt));
  const windowState = data?.window;

  return (
    <div className="flex h-full flex-col bg-surface text-slate-200">
      {/* Header — title, live/recap badge, preset selector, refresh */}
      <div className="flex items-center justify-between gap-2 border-b border-default px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <Radar size={14} className="flex-shrink-0 text-sky-500" />
          <h2 className="truncate text-xs font-semibold uppercase tracking-wide text-slate-200">
            Scanner
          </h2>
          {windowState && (
            <span
              className={cn(
                'flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide',
                windowState === 'open'
                  ? 'bg-emerald-500/15 text-emerald-400'
                  : 'bg-slate-600/30 text-slate-400',
              )}
              title={
                windowState === 'open'
                  ? 'Live — inside the 9:15–10:15 ET open window'
                  : `Recap of the last session's 9:15–10:15 ET window${data?.sessionDate ? ` (${data.sessionDate})` : ''}`
              }
            >
              {windowState === 'open' ? 'Live' : 'Recap'}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => void load({ silent: true })}
          disabled={refreshing}
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-sky-500/10 hover:text-sky-400 disabled:opacity-50"
          title="Refresh now"
          aria-label="Refresh scanner"
        >
          <RefreshCw size={12} className={cn(refreshing && 'animate-spin')} />
        </button>
      </div>

      {/* Preset selector */}
      <div className="flex items-center gap-1 border-b border-default px-3 py-1.5">
        <span className="mr-1 text-[9px] font-medium uppercase tracking-wide text-slate-500">
          Universe
        </span>
        {SCOPE_PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setScope(p.value)}
            title={p.title}
            className={cn(
              'rounded-md px-2 py-0.5 text-[10px] font-medium transition-colors',
              scope === p.value
                ? 'bg-sky-500 text-white'
                : 'text-slate-400 hover:bg-card-bg hover:text-slate-200',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Window selector — the scored open window vs a live last-30-min feed */}
      <div className="flex items-center gap-1 border-b border-default px-3 py-1.5">
        <span className="mr-1 text-[9px] font-medium uppercase tracking-wide text-slate-500">
          Window
        </span>
        {WINDOW_PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setWinMode(p.value)}
            title={p.title}
            className={cn(
              'rounded-md px-2 py-0.5 text-[10px] font-medium transition-colors',
              winMode === p.value
                ? 'bg-sky-500 text-white'
                : 'text-slate-400 hover:bg-card-bg hover:text-slate-200',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-surface">
        {loading && candidates.length === 0 ? (
          <LoadingState />
        ) : error && candidates.length === 0 ? (
          <ErrorState onRetry={() => void load()} />
        ) : candidates.length === 0 ? (
          <EmptyState windowState={windowState} scope={scope} noWatchlist={noWatchlist} />
        ) : (
          <ul className="divide-y divide-default">
            {candidates.map((c) => (
              <ScannerRow
                key={c.ticker}
                c={c}
                active={c.ticker.toUpperCase() === activeSymbol.toUpperCase()}
                onSelect={() => onSelectSymbol(c.ticker)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ScannerRow({
  c,
  active,
  onSelect,
}: {
  c: OpportunityCandidate;
  active: boolean;
  onSelect: () => void;
}) {
  const up = c.changePct >= 0;
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? 'true' : undefined}
        className={cn(
          'flex w-full items-center gap-2 px-3 py-2 text-left transition-colors',
          active ? 'bg-sky-500/10' : 'hover:bg-card-bg',
          active && 'border-l-2 border-sky-500 pl-[10px]',
        )}
        title={`${c.ticker} · ${reasonLabel(c.reason)} · composite ${Math.round(c.score)}`}
      >
        {/* Ticker + reason tag */}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span
              className={cn(
                'truncate font-mono text-sm font-semibold',
                active ? 'text-sky-300' : 'text-slate-200',
              )}
            >
              {c.ticker}
            </span>
            <span
              className={cn(
                'flex-shrink-0 rounded px-1 py-0.5 text-[8px] font-medium uppercase tracking-wide',
                SEVERITY_TAG[c.severity] ?? 'bg-slate-500/15 text-slate-300 border border-slate-500/30',
              )}
            >
              {reasonLabel(c.reason)}
            </span>
          </div>
          <span className="text-[10px] tabular-nums text-slate-500">
            {fmtTime(c.detectedAt)} ET
          </span>
        </div>

        {/* Last / %chg */}
        <div className="flex-shrink-0 text-right">
          <div className="font-mono text-xs tabular-nums text-slate-200">{fmtPrice(c.price)}</div>
          <div
            className={cn(
              'font-mono text-[10px] tabular-nums',
              up ? 'text-emerald-400' : 'text-rose-400',
            )}
          >
            {fmtPct(c.changePct)}
          </div>
        </div>

        {/* RVOL */}
        <div className="w-12 flex-shrink-0 text-right">
          <div className="text-[9px] uppercase tracking-wide text-slate-500">RVOL</div>
          <div className="font-mono text-xs tabular-nums text-amber-300">{fmtRvol(c.currentRvol)}</div>
        </div>
      </button>
    </li>
  );
}

function LoadingState() {
  return (
    <ul className="divide-y divide-default">
      {Array.from({ length: 8 }).map((_, i) => (
        <li key={i} className="flex items-center gap-2 px-3 py-2.5">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <div className="h-3 w-16 animate-pulse rounded bg-card-bg" />
            <div className="h-2 w-24 animate-pulse rounded bg-card-bg" />
          </div>
          <div className="h-6 w-10 animate-pulse rounded bg-card-bg" />
          <div className="h-6 w-10 animate-pulse rounded bg-card-bg" />
        </li>
      ))}
    </ul>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-4 py-10 text-center">
      <AlertTriangle size={20} className="text-rose-400/70" />
      <p className="text-xs text-slate-400">Couldn&apos;t load the scanner right now.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-1 rounded-md border border-default px-3 py-1 text-[11px] text-slate-300 transition-colors hover:border-sky-500/50 hover:text-sky-300"
      >
        Retry
      </button>
    </div>
  );
}

function EmptyState({ windowState, scope, noWatchlist }: { windowState?: 'open' | 'recap'; scope: Scope; noWatchlist: boolean }) {
  if (noWatchlist) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1.5 px-4 py-10 text-center">
        <Radar size={22} className="text-slate-600" />
        <p className="text-xs text-slate-400">No symbols in your watchlists.</p>
        <p className="text-[10px] text-slate-500">Add symbols to a watchlist to scan just your names.</p>
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col items-center justify-center gap-1.5 px-4 py-10 text-center">
      <Radar size={22} className="text-slate-600" />
      <p className="text-xs text-slate-400">
        {scope === 'watchlist'
          ? `None of your watchlist symbols qualified${windowState === 'recap' ? ' last session' : ' yet'}.`
          : `No qualifying setups${windowState === 'recap' ? ' last session' : ' yet'}.`}
      </p>
      <p className="text-[10px] text-slate-500">
        The scanner lights up during the 9:15–10:15 ET open.
      </p>
    </div>
  );
}

export default ScannerPanel;
